import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-20 bg-background-50">
        <article>
          {/* Article Header */}
          <header className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          </header>

          {/* Article Content */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-20 relative z-10">
            <div className="bg-background-50 rounded-lg p-6 md:p-10">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  {article.category}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
              </nav>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-500">{article.date}</span>
                <span className="text-sm text-foreground-500">{article.author}</span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>

              {/* Title */}
              <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
                {article.title}
              </h1>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Article Body */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{related.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.image,
            publisher: {
              '@type': 'Organization',
              name: 'aaa日韩',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.aaarihan.com/article/${article.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.aaarihan.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `https://www.aaarihan.com/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}